import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Save } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { api, ApiRequestError } from "@/lib/api"
import { cn } from "@/lib/utils"
import type { ArealResponse } from "@fohet/caravan-booker-shared/types"
import { inputClass, labelClass, sectionClass, primaryBtnClass, sectionHeaderClass } from "@/lib/styleClasses"

interface BasicInfoFormValues {
    name: string
    spotCount: number
    currency: string
    visibility: "draft" | "unlisted" | "public"
    releaseCodesImmediately: boolean
}

export const BasicInfoSection = ({ areal }: { areal: ArealResponse }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const { register, handleSubmit, formState: { isDirty } } = useForm<BasicInfoFormValues>({
        values: {
            name: areal.name,
            spotCount: areal.spotCount,
            currency: areal.currency,
            visibility: areal.visibility,
            releaseCodesImmediately: areal.releaseCodesImmediately,
        },
    })

    const saveMutation = useMutation({
        mutationFn: (values: BasicInfoFormValues) =>
            api.patch<ArealResponse>(`/areals/${areal.id}`, {
                name: values.name,
                spotCount: Number(values.spotCount),
                currency: values.currency,
                visibility: values.visibility,
                releaseCodesImmediately: values.releaseCodesImmediately,
            }),
        onSuccess: () => {
            toast.success(t("admin.settings.toastSaved"))
            queryClient.invalidateQueries({ queryKey: ["admin", "areals"] })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.toastFailed"))
        },
    })

    return (
        <div className={sectionClass}>
            <h2 className={sectionHeaderClass}>{t("admin.settings.sectionBasic")}</h2>
            <form onSubmit={handleSubmit((v) => saveMutation.mutate(v))} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                        <label className={labelClass}>{t("admin.settings.fieldName")}</label>
                        <input {...register("name", { required: true })} className={inputClass} />
                    </div>
                    <div className="col-span-2">
                        <label className={labelClass}>{t("admin.settings.fieldSlug")}</label>
                        <input
                            value={areal.slug}
                            readOnly
                            className={cn(inputClass, "bg-slate-100 text-slate-500 cursor-not-allowed")}
                        />
                        <p className="text-xs text-slate-400 font-bold mt-1">{t("admin.settings.fieldSlugLocked")}</p>
                    </div>
                    <div>
                        <label className={labelClass}>{t("admin.settings.fieldSpotCount")}</label>
                        <input type="number" min="1" {...register("spotCount", { valueAsNumber: true })} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>{t("admin.settings.fieldCurrency")}</label>
                        <input {...register("currency")} placeholder="CZK" className={inputClass} />
                    </div>
                    <div className="col-span-2">
                        <label className={labelClass}>{t("admin.settings.fieldVisibility")}</label>
                        <select {...register("visibility")} className={inputClass}>
                            <option value="draft">{t("admin.settings.visibilityDraft")}</option>
                            <option value="unlisted">{t("admin.settings.visibilityUnlisted")}</option>
                            <option value="public">{t("admin.settings.visibilityPublic")}</option>
                        </select>
                    </div>
                    <div className="col-span-2 flex items-start gap-3">
                        <input
                            id="releaseCodesImmediately"
                            type="checkbox"
                            {...register("releaseCodesImmediately")}
                            className="mt-0.5 size-4 accent-brand cursor-pointer"
                        />
                        <div>
                            <label
                                htmlFor="releaseCodesImmediately"
                                className="text-sm font-black text-slate-700 cursor-pointer"
                            >
                                {t("admin.settings.releaseCodesImmediately")}
                            </label>
                            <p className="text-xs text-slate-400 font-bold mt-0.5">
                                {t("admin.settings.releaseCodesImmediatelyHint")}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="flex justify-end">
                    <button type="submit" disabled={saveMutation.isPending || !isDirty} className={primaryBtnClass}>
                        <Save className="size-3.5" />
                        {saveMutation.isPending ? t("admin.settings.saving") : t("admin.settings.save")}
                    </button>
                </div>
            </form>
        </div>
    )
}
